import React from "react";

import PublishIcon from "@src/assets/icons/envelope.svg";
import SendIcon from "@src/assets/icons/send.svg";
import TrashIcon from "@src/assets/icons/trash.svg";
import UndoIcon from "@src/assets/icons/undo.svg";
import { TABLE_ACTION_TYPE, RESOURCE_PLAN_TABS } from "@src/constants";
import {
  AllocationRowData,
  ResourcePlanTableRowData,
} from "@src/types/role_request_types";
import { DefaultOptionType } from "antd/es/select/index";
import _ from "lodash";

/**
 * Get the send options
 *
 * @param {boolean} isUndoOptionDisabled - If the undo option is disabled
 * @param {boolean} isSendRequestDisabled - If the send request option is disabled
 *
 * @returns {DefaultOptionType[]} - The send options
 */
export function getSendOptions(
  isUndoOptionDisabled: boolean,
  isSendRequestDisabled: boolean
): DefaultOptionType[] {
  return [
    {
      value: TABLE_ACTION_TYPE.UNDO,
      icon: <UndoIcon />,
      desc: "Undo changes on selected request(s)",
      disabled: isUndoOptionDisabled,
    },
    {
      value: TABLE_ACTION_TYPE.SEND,
      icon: <SendIcon />,
      desc: "Send selected request(s)",
      disabled: isSendRequestDisabled,
    },
    {
      value: TABLE_ACTION_TYPE.DELETE,
      icon: <TrashIcon className="delete-icon" />,
      desc: "Delete request ",
    },
  ];
}

/**
 * Get the draft options
 *
 * @param {boolean} isPublishRequestDisabled - If the publish request option is disabled
 *
 * @returns {DefaultOptionType[]} - The draft options
 */
export function getDraftOptions(
  isPublishRequestDisabled: boolean
): DefaultOptionType[] {
  return [
    {
      value: TABLE_ACTION_TYPE.SEND,
      icon: <PublishIcon />,
      desc: "Publish & Send Draft Request",
      disabled: isPublishRequestDisabled,
    },
    {
      value: TABLE_ACTION_TYPE.DELETE,
      icon: <TrashIcon className="delete-icon" />,
      desc: "Delete request ",
    },
  ];
}

/**
 * Check if the option is disabled
 *
 * @param {string} tabType - The type of the request
 * @param {DefaultOptionType} option - The option to check
 * @param {boolean} isDraftOptionDisabled - If the draft option is disabled
 * @param {boolean} isSendOptionDisabled - If the send option is disabled
 * @param {boolean} isUndoOptionDisabled - If the undo option is disabled
 *
 * @returns {boolean} - True if the option is disabled, false otherwise
 */
export function isOptionDisabled(
  tabType: string,
  option: DefaultOptionType,
  isDraftOptionDisabled: boolean,
  isSendOptionDisabled: boolean,
  isUndoOptionDisabled: boolean
): boolean {
  const isInDraftTab: boolean = tabType === RESOURCE_PLAN_TABS.DRAFTS;
  const isInInboxTab: boolean =
    tabType === RESOURCE_PLAN_TABS.RESOURCE_PLAN_INBOX;

  if (option.data.value === TABLE_ACTION_TYPE.SEND) {
    return (
      (isInDraftTab && isDraftOptionDisabled) ||
      (isInInboxTab && isSendOptionDisabled)
    );
  }

  if (option.data.value === TABLE_ACTION_TYPE.UNDO) {
    return isInInboxTab && isUndoOptionDisabled;
  }

  return false;
}

/**
 * Check if the undo option is disabled
 *
 * @param {ResourcePlanTableRowData[]} selectedSendRequests - The selected send requests
 *
 * @returns {boolean} - True if the undo option is disabled, false otherwise
 */
export function isUndoOptionDisabled(
  selectedSendRequests: ResourcePlanTableRowData[]
): boolean {
  const allAllocations: AllocationRowData[] =
    getAllocationsOfRequest(selectedSendRequests);

  return !hasChanges(allAllocations);
}

/**
 * Check if the send option is disabled
 *
 * @param {ResourcePlanTableRowData[]} selectedSendRequests - The selected send requests
 *
 * @returns {boolean} - True if the send option is disabled, false otherwise
 */
export function isSendOptionDisabled(
  selectedSendRequests: ResourcePlanTableRowData[]
): boolean {
  const allAllocations: AllocationRowData[] =
    getAllocationsOfRequest(selectedSendRequests);

  return (
    hasEmptyRequest(allAllocations) ||
    isUpdatedValueNotInRange(allAllocations) ||
    !hasChanges(allAllocations) ||
    isRequestWithoutTeamLead(selectedSendRequests)
  );
}

/**
 * Will check if the table data has changed. This is used to show the user that the changes have been made.
 *
 * @param requests The selected requests
 * @returns true if the table data has changed, false otherwise
 */
export function hasTableDataChanged(
  requests: ResourcePlanTableRowData[]
): boolean {
  const allocations: AllocationRowData[] = getAllocationsOfRequest(requests);

  return hasChanges(allocations);
}

/**
 * Check if the draft option is disabled. The draft option will be disabled when the request is completely empty or
 * has invalid changes lik out of range percentages.
 *
 * @param {ResourcePlanTableRowData[]} selectedDraftRequests The selected draft requests
 *
 * @returns {boolean}  True if the draft option is disabled, false otherwise
 */
export function isDraftOptionDisabled(
  selectedDraftRequests: ResourcePlanTableRowData[]
): boolean {
  const allocations: AllocationRowData[] = getAllocationsOfRequest(
    selectedDraftRequests
  );

  return (
    hasEmptyRequest(allocations) ||
    isUpdatedValueNotInRange(allocations) ||
    isRequestWithoutTeamLead(selectedDraftRequests)
  );
}

/**
 * Wll check if the request got updated and if so than the undo can be enabled.
 *
 * @param allAllocations all allocations of the selected request.
 */
export function hasChanges(allAllocations: AllocationRowData[]): boolean {
  return _.some(
    allAllocations,
    (allocation: AllocationRowData) =>
      allocation.updatedPercentage != null &&
      allocation.updatedPercentage !== allocation.percentage
  );
}

/**
 * Checks if a team lead is assigned to the standard role of the request.
 *
 * @param selectedSendRequests the requests the user selected
 */
export function isRequestWithoutTeamLead(
  selectedSendRequests: ResourcePlanTableRowData[]
): boolean {
  return selectedSendRequests.some((request) => {
    return !request.teamLead;
  });
}

/**
 * Get all allocation for the selected send requests
 *
 * @param {ResourcePlanTableRowData[]} selectedSendRequests - The selected send requests
 *
 * @returns {AllocationRowData[]} - The allocation changes
 */
export function getAllocationsOfRequest(
  selectedSendRequests: ResourcePlanTableRowData[]
): AllocationRowData[] {
  return _.flatMap(
    selectedSendRequests,
    (request: ResourcePlanTableRowData) => {
      return Array.from(request.allocations.values());
    }
  );
}

/**
 * Check if the updated value is invalid
 *
 * @param {AllocationRowData[]} allAllocations - The allocation changes
 *
 * @returns {boolean} - True if the updated value is invalid, false otherwise
 */
export function isUpdatedValueNotInRange(
  allAllocations: AllocationRowData[]
): boolean {
  return _.some(
    allAllocations,
    (allocation: AllocationRowData) =>
      allocation.updatedPercentage != null &&
      (allocation.updatedPercentage < 0 || allocation.updatedPercentage > 100)
  );
}

/**
 * Check if one of the allocations is completely empty.
 *
 * @param {AllocationRowData[]} allAllocations - The allocation changes
 *
 * @returns {boolean} - True if all allocations got deleted in the request
 */
export function hasEmptyRequest(allAllocations: AllocationRowData[]): boolean {
  return _.every(
    allAllocations,
    (allocation: AllocationRowData) => allocation.updatedPercentage === 0
  );
}
